import type {AnalyticsSink, LogLevel, Props} from './types';

const BUFFER_LIMIT = 100;

type Call = (sink: AnalyticsSink) => void;

let sinks: AnalyticsSink[] = [];
let globalProps: Props = {};
// Calls made before any sink is registered; flushed on first registration.
let buffer: Call[] = [];

const safe = (sink: AnalyticsSink, call: Call) => {
  try {
    call(sink);
  } catch (e) {
    // One failing backend must never break the others or the app.
  }
};

const dispatch = (call: Call) => {
  if (sinks.length === 0) {
    if (buffer.length >= BUFFER_LIMIT) {
      buffer.shift();
    }
    buffer.push(call);
    return;
  }
  sinks.forEach(sink => safe(sink, call));
};

const withGlobals = (props?: Props): Props => ({...globalProps, ...props});

export const analytics = {
  registerSink(sink: AnalyticsSink) {
    sinks.push(sink);
    const pending = buffer;
    buffer = [];
    pending.forEach(call => safe(sink, call));
  },

  /** Props merged into every track/page/log/error/measure call. */
  setGlobalProps(props: Props) {
    globalProps = {...globalProps, ...props};
  },

  track(name: string, props?: Props) {
    const merged = withGlobals(props);
    dispatch(s => s.track?.(name, merged));
  },

  page(name: string, props?: Props) {
    const merged = withGlobals(props);
    dispatch(s => s.page?.(name, merged));
  },

  identify(userId: string, traits: Props = {}) {
    dispatch(s => s.identify?.(userId, traits));
  },

  setUserProperties(props: Props) {
    dispatch(s => s.setUserProperties?.(props));
  },

  log(level: LogLevel, message: string, context?: Props) {
    const merged = withGlobals(context);
    dispatch(s => s.log?.(level, message, merged));
  },

  error(err: unknown, context?: Props) {
    const merged = withGlobals(context);
    dispatch(s => s.error?.(err, merged));
  },

  measure(name: string, value: number, attrs?: Props) {
    const merged = withGlobals(attrs);
    dispatch(s => s.measure?.(name, value, merged));
  },

  /** Clears global props and tells sinks to drop the user (e.g. on logout). */
  reset() {
    globalProps = {};
    dispatch(s => s.reset?.());
  },

  /** Test helper: drop all sinks, globals and buffered calls. */
  _clear() {
    sinks = [];
    globalProps = {};
    buffer = [];
  },
};
